if (!customElements.get("media-gallery")) {
  window.__joydecoGalleryPatch = "r13-20260805";
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        const currentVariantMedia = this.querySelector(
          ".is-current-variant-media",
        );

        this.lastVariantMediaId = currentVariantMedia
          ? parseInt(currentVariantMedia.dataset.mediaId, 10)
          : null;
        this.stackSeeMoreLimit = parseInt(
          this.dataset.stackSeeMoreLimit || "7",
          10,
        );
        this.galleryUpdateToken = 0;
        this.externalSyncPausedUntil = 0;
        this.boundOnSlideChanged = this.onSlideChanged.bind(this);
        this.boundOnThumbnailClick = this.onThumbnailClick.bind(this);
        this.boundSyncStackSeeMore = this.syncStackSeeMore.bind(this);
        this.mobileMediaQuery = window.matchMedia("(max-width: 749px)");

        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((thumbnail) => {
              thumbnail.addEventListener("click", this.boundOnThumbnailClick);
            });
        }

        this.addEventListener("sliderChanged", this.boundOnSlideChanged);

        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener(
            "change",
            this.boundSyncStackSeeMore,
          );
        } else if (this.mobileMediaQuery.addListener) {
          this.mobileMediaQuery.addListener(this.boundSyncStackSeeMore);
        }

        this.syncStackSeeMore();
        this.initExternalOptionBridge();
        this.initResizeGuard();
        this.initImageSourceGuard();
      }

      disconnectedCallback() {
        this.removeEventListener("sliderChanged", this.boundOnSlideChanged);

        if (this.imageGuardObserver) {
          this.imageGuardObserver.disconnect();
          this.imageGuardObserver = null;
        }

        if (this.resizeGuard) {
          this.resizeGuard.disconnect();
          this.resizeGuard = null;
        }
        if (this.resizeGuardTimer) {
          clearTimeout(this.resizeGuardTimer);
          this.resizeGuardTimer = null;
        }

        if (this.boundExternalChange) {
          document.removeEventListener(
            "change",
            this.boundExternalChange,
            true,
          );
          document.removeEventListener(
            "click",
            this.boundExternalClick,
            true,
          );
        }

        if (this.idInputObserver) {
          this.idInputObserver.disconnect();
          this.idInputObserver = null;
        }

        if (this.externalSyncTimer) {
          clearTimeout(this.externalSyncTimer);
          this.externalSyncTimer = null;
        }

        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((thumbnail) => {
              thumbnail.removeEventListener(
                "click",
                this.boundOnThumbnailClick,
              );
            });
        }

        if (this.mobileMediaQuery?.removeEventListener) {
          this.mobileMediaQuery.removeEventListener(
            "change",
            this.boundSyncStackSeeMore,
          );
        } else if (this.mobileMediaQuery?.removeListener) {
          this.mobileMediaQuery.removeListener(this.boundSyncStackSeeMore);
        }
      }

      initResizeGuard() {
        if (typeof ResizeObserver === "undefined") return;

        this.lastGuardWidths = new WeakMap();
        this.resizeGuard = new ResizeObserver((entries) => {
          let changed = false;
          for (const entry of entries) {
            const width = entry.contentRect?.width || 0;
            const prev = this.lastGuardWidths.get(entry.target) || 0;
            if (Math.abs(width - prev) > 1) {
              this.lastGuardWidths.set(entry.target, width);
              if (prev > 0) changed = true;
            }
          }
          if (!changed) return;
          if (this.resizeGuardTimer) clearTimeout(this.resizeGuardTimer);
          this.resizeGuardTimer = setTimeout(() => {
            this.resizeGuardTimer = null;
            this.remetricSliders();
          }, 150);
        });

        const viewerContainer = this.viewer?.querySelector(".slider-container");
        const thumbContainer =
          this.thumbnails?.querySelector(".slider-container");
        if (viewerContainer) this.resizeGuard.observe(viewerContainer);
        if (thumbContainer) this.resizeGuard.observe(thumbContainer);
      }

      remetricSliders() {
        const remetric = (slider) => {
          if (!slider) return;

          if (!slider.initSliderStatus) {
            if (
              typeof slider.install === "function" &&
              this.getVisibleViewerItems().length > 1
            ) {
              slider.install();
            }
            return;
          }

          const currentElement = slider.sliderItems?.[slider.index] || null;
          if (typeof slider.updateSliderMetrics === "function") {
            slider.updateSliderMetrics();
          }
          if (currentElement) {
            const idx = (slider.sliderItems || []).indexOf(currentElement);
            if (idx >= 0) slider.index = idx;
          }
          if (typeof slider.performSlide === "function") {
            if (typeof slider.disableTransition === "function") {
              slider.disableTransition();
            }
            slider.performSlide();
            if (typeof slider.enableTransition === "function") {
              setTimeout(
                slider.enableTransition.bind(slider),
                slider.TRANSITION_DURING || 300,
              );
            }
          }
        };

        remetric(this.viewer);
        remetric(this.thumbnails);
      }

      getGuardableImages() {
        return Array.from(
          this.querySelectorAll(
            ".product-media-item img, .thumbnail-list-item img",
          ),
        );
      }

      captureImageSources() {
        this.getGuardableImages().forEach((img) => {
          if (
            img.hasAttribute("data-guard-src") ||
            img.hasAttribute("data-guard-srcset")
          ) {
            return;
          }
          const src =
            img.getAttribute("data-src") || img.getAttribute("src") || "";
          const srcset =
            img.getAttribute("data-srcset") ||
            img.getAttribute("srcset") ||
            "";
          if (src && !src.startsWith("data:")) {
            img.setAttribute("data-guard-src", src);
          }
          if (srcset) {
            img.setAttribute("data-guard-srcset", srcset);
          }
        });
      }

      normalizeImagePath(value) {
        const raw = String(value || "").trim();
        if (!raw) return "";
        try {
          return new URL(raw, window.location.href).pathname;
        } catch (e) {
          return raw.split("?")[0];
        }
      }

      firstSrcsetPath(value) {
        const first = String(value || "")
          .split(",")[0]
          .trim()
          .split(/\s+/)[0];
        return this.normalizeImagePath(first);
      }

      restoreGuardedImage(img) {
        const expectedSrc = img.getAttribute("data-guard-src");
        if (!expectedSrc) return false;

        const expectedSrcset = img.getAttribute("data-guard-srcset") || "";
        const currentSrc = img.getAttribute("src") || "";
        const currentSrcset = img.getAttribute("srcset") || "";

        const srcTampered =
          currentSrc &&
          !currentSrc.startsWith("data:") &&
          this.normalizeImagePath(currentSrc) !==
            this.normalizeImagePath(expectedSrc);
        const srcsetTampered =
          currentSrcset &&
          expectedSrcset &&
          this.firstSrcsetPath(currentSrcset) !==
            this.firstSrcsetPath(expectedSrcset);
        const srcsetInjected = currentSrcset && !expectedSrcset;

        if (!srcTampered && !srcsetTampered && !srcsetInjected) return false;

        img.setAttribute("src", expectedSrc);
        if (expectedSrcset) {
          img.setAttribute("srcset", expectedSrcset);
        } else {
          img.removeAttribute("srcset");
        }
        return true;
      }

      restoreAllGuardedImages() {
        let restored = false;
        this.getGuardableImages().forEach((img) => {
          if (this.restoreGuardedImage(img)) restored = true;
        });
        return restored;
      }

      initImageSourceGuard() {
        this.captureImageSources();
        this.restoreAllGuardedImages();

        if (typeof MutationObserver === "undefined") return;

        this.imageGuardObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            const img = mutation.target;
            if (!(img instanceof HTMLImageElement)) return;
            if (!img.closest(".product-media-item, .thumbnail-list-item")) {
              return;
            }
            this.restoreGuardedImage(img);
          });
        });
        this.imageGuardObserver.observe(this, {
          subtree: true,
          attributes: true,
          attributeFilter: ["src", "srcset"],
        });
      }

      parseProductGalleryMap() {
        const script = this.querySelector(
          "script[data-product-gallery-map]",
        );
        if (!script?.textContent?.trim()) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
      }

      initExternalOptionBridge() {
        this.productGalleryMap = this.parseProductGalleryMap();
        this.boundExternalChange = () => this.scheduleExternalSync();
        this.boundExternalClick = (event) => {
          if (
            event.target?.closest?.(
              "#bcpo, .bcpo-image-parent, .bcpo-front-image-option, .bcpo-front-image-label",
            )
          ) {
            this.scheduleExternalSync();
          }
        };

        document.addEventListener("change", this.boundExternalChange, true);
        document.addEventListener("click", this.boundExternalClick, true);

        const idInputs = document.querySelectorAll(
          'form[action*="/cart/add"] input[name="id"], product-form input[name="id"]',
        );
        idInputs.forEach((input) => {
          input.addEventListener("change", this.boundExternalChange);
          input.addEventListener("input", this.boundExternalChange);
        });

        if (idInputs[0] && typeof MutationObserver !== "undefined") {
          this.idInputObserver = new MutationObserver(() =>
            this.scheduleExternalSync(),
          );
          idInputs.forEach((input) => {
            this.idInputObserver.observe(input, {
              attributes: true,
              attributeFilter: ["value"],
            });
          });
        }

        try {
          const self = this;
          const wrapHistory = (type) => {
            const original = history[type];
            if (typeof original !== "function") return;
            history[type] = function () {
              const result = original.apply(this, arguments);
              self.scheduleExternalSync(30);
              return result;
            };
          };
          wrapHistory("replaceState");
          wrapHistory("pushState");
          window.addEventListener("popstate", this.boundExternalChange);
        } catch (e) {}

        this.lastExternalKey = "";
        this.scheduleExternalSync(0);
        this.scheduleExternalSync(600);
      }

      scheduleExternalSync(delay = 80) {
        if (this.externalSyncTimer) clearTimeout(this.externalSyncTimer);
        this.externalSyncTimer = setTimeout(() => {
          this.externalSyncTimer = null;
          if (Date.now() < this.externalSyncPausedUntil) return;
          this.syncFromExternalOptions();
        }, delay);
      }

      normalizeOptionText(value) {
        return String(value || "")
          .replace(/\u00a0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }

      readExternalColor() {
        const selectors = [
          "#bcpo-select-option-0 input:checked",
          "#bcpo .bcpo-auto-images input:checked",
          "#bcpo .selector-wrapper.bcpo-auto-images input:checked",
          '#bcpo input[type="radio"]:checked',
          '#bcpo input.bcpo-front-image-option:checked',
        ];

        for (const selector of selectors) {
          const input = document.querySelector(selector);
          const value = this.normalizeOptionText(
            input?.value || input?.getAttribute("data-value"),
          );
          if (value) return value;
        }

        const selectedParent = document.querySelector(
          "#bcpo-select-option-0 .bcpo-image-parent:has(input:checked) label, #bcpo .bcpo-auto-images .bcpo-image-parent:has(input:checked) label",
        );
        const title =
          selectedParent?.getAttribute("title") ||
          selectedParent?.getAttribute("aria-label") ||
          selectedParent?.dataset?.title;
        const fromTitle = this.normalizeOptionText(title);
        if (fromTitle) return fromTitle;

        const colorLabel = Array.from(
          document.querySelectorAll("#bcpo .bcpo-title, #bcpo label, #bcpo span"),
        ).find((el) => /color\s*:/i.test(el.textContent || ""));
        if (colorLabel) {
          const text = this.normalizeOptionText(colorLabel.textContent);
          const matched = text.match(/color\s*:\s*(.+)$/i);
          if (matched?.[1]) return this.normalizeOptionText(matched[1]);
        }

        return "";
      }

      readExternalVariantId() {
        const input = document.querySelector(
          'form[action*="/cart/add"] input[name="id"], product-form input[name="id"]',
        );
        return input?.value ? String(input.value) : "";
      }

      resolveExternalMedia(color, variantId) {
        const map = this.productGalleryMap;
        if (!map) return { mediaId: null, color };

        const normalizedColor = this.normalizeOptionText(color);
        if (normalizedColor && map.byColor) {
          if (map.byColor[normalizedColor] != null) {
            return {
              mediaId: map.byColor[normalizedColor],
              color: normalizedColor,
            };
          }
          const lower = normalizedColor.toLowerCase();
          for (const [name, mediaId] of Object.entries(map.byColor)) {
            if (String(name).toLowerCase() === lower && mediaId != null) {
              return { mediaId, color: name };
            }
          }
        }

        if (variantId && map.byVariantId?.[variantId]) {
          const entry = map.byVariantId[variantId];
          const entryColor = this.normalizeOptionText(entry.color || "");
          if (
            !normalizedColor ||
            entryColor.toLowerCase() === normalizedColor.toLowerCase()
          ) {
            return {
              mediaId: entry.mediaId || null,
              color: entryColor || normalizedColor,
            };
          }
        }

        return { mediaId: null, color: normalizedColor };
      }

      syncFromExternalOptions() {
        if (!this.productGalleryMap) {
          this.productGalleryMap = this.parseProductGalleryMap();
        }
        if (!this.productGalleryMap) return;

        const color = this.readExternalColor();
        const variantId = this.readExternalVariantId();
        const resolved = this.resolveExternalMedia(color, variantId);
        const key = `${resolved.color}::${resolved.mediaId || ""}`;
        if (key === this.lastExternalKey) return;
        this.lastExternalKey = key;

        if (resolved.mediaId) {
          this.updateGallery(resolved.mediaId, resolved.color, {
            option1: resolved.color,
            options: [resolved.color],
            featured_media: { id: resolved.mediaId },
          });
          return;
        }

        if (resolved.color) {
          this.applyColorGroup(resolved.color);
        }
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      get ifMediaStack() {
        return this.hasAttribute("data-media-stack");
      }

      get stackSeeMore() {
        return this.querySelector(".product-media-stack-see-more");
      }

      get stackSeeMoreOpener() {
        return this.querySelector(".product-media-stack-see-more-opener");
      }

      get stackMediaItems() {
        return Array.from(
          this.viewer?.querySelectorAll(".product-media-item") || [],
        );
      }

      collectMediaGroups() {
        const groups = new Set();
        this.viewer
          ?.querySelectorAll("[data-media-group]")
          .forEach((element) => {
            const group = this.normalizeGroupName(element.dataset.mediaGroup);
            if (group) groups.add(group);
          });
        return groups;
      }

      normalizeGroupName(value) {
        const raw = String(value || "")
          .replace(/\r/g, "")
          .replace(/\u00a0/g, " ")
          .trim();
        if (!raw) return "";
        const firstLine = raw.split("\n")[0].trim();
        if (firstLine.startsWith("###")) return firstLine.replace(/\s+/g, " ");
        return firstLine.replace(/\s+/g, " ");
      }

      findMatchingGroup(groups, target) {
        const normalizedTarget = this.normalizeGroupName(target);
        if (!normalizedTarget || !groups?.size) return null;
        if (groups.has(normalizedTarget)) return normalizedTarget;

        const lowerTarget = normalizedTarget.toLowerCase();
        for (const group of groups) {
          if (group.toLowerCase() === lowerTarget) return group;
        }
        return null;
      }

      resolveActiveGroup(colorValue, currentVariantMediaId, variant) {
        const groups = this.collectMediaGroups();
        if (!groups.size) return null;

        let color = this.normalizeGroupName(colorValue).replace(/^###/, "");
        if (!color && variant) {
          color = this.normalizeGroupName(
            variant.option1 ||
              (Array.isArray(variant.options) ? variant.options[0] : ""),
          );
        }

        if (color) {
          const colorGroup = this.findMatchingGroup(groups, `###${color}`);
          if (colorGroup) return colorGroup;
        }

        const featuredGroup = this.normalizeGroupName(
          this.viewer?.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          )?.dataset.mediaGroup,
        );
        if (featuredGroup && groups.has(featuredGroup)) return featuredGroup;

        const defaultGroup = this.findMatchingGroup(groups, "###default");
        if (defaultGroup) return defaultGroup;

        return featuredGroup || null;
      }

      applyActiveGroup(groupName) {
        const activeGroup = this.normalizeGroupName(groupName);

        this.viewer
          ?.querySelectorAll(".is-current-variant-media-group")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media-group"),
          );

        if (activeGroup) {
          this.viewer
            ?.querySelectorAll("[data-media-group]")
            .forEach((element) => {
              if (
                this.normalizeGroupName(element.dataset.mediaGroup) ===
                activeGroup
              ) {
                element.classList.add("is-current-variant-media-group");
              }
            });
        }

        if (!this.thumbnails) return;

        this.thumbnails
          .querySelectorAll(".is-current-variant-thumbnail-group")
          .forEach((element) =>
            element.classList.remove("is-current-variant-thumbnail-group"),
          );

        if (activeGroup) {
          this.thumbnails
            .querySelectorAll("[data-thumbnail-group]")
            .forEach((element) => {
              if (
                this.normalizeGroupName(element.dataset.thumbnailGroup) ===
                activeGroup
              ) {
                element.classList.add("is-current-variant-thumbnail-group");
              }
            });
        }
      }

      applyColorGroup(colorValue) {
        if (!this.hasAttribute("data-hide-variants")) return;

        this.restoreAllGuardedImages();
        const groupName = this.resolveActiveGroup(
          colorValue,
          this.lastVariantMediaId,
        );
        this.applyActiveGroup(groupName);
        void this.offsetHeight;
        this.ensureVisibleImagesLoaded();

        if (this.ifMediaStack && !webvista.isMobileScreen()) {
          this.syncStackSeeMore();
          return;
        }

        this.softRefreshSliders(
          this.lastVariantMediaId != null
            ? String(this.lastVariantMediaId)
            : this.getVisibleViewerItems()[0]?.dataset?.mediaId,
        );

        const featuredMediaId =
          this.lastVariantMediaId != null
            ? String(this.lastVariantMediaId)
            : this.getVisibleViewerItems()[0]?.dataset?.mediaId;
        if (featuredMediaId) {
          this.focusFeaturedMedia(featuredMediaId);
        }
      }

      isElementVisible(element) {
        if (!element) return false;
        return window.getComputedStyle(element).display !== "none";
      }

      removeSliderClones(slider) {
        if (!slider) return;
        slider
          .querySelectorAll(".slider-slide--clone")
          .forEach((element) => element.remove());
      }

      resetSlider(slider) {
        if (!slider) return;

        this.removeSliderClones(slider);

        if (slider.initSliderStatus && typeof slider.uninstall === "function") {
          slider.uninstall();
        } else {
          if (typeof slider.clearTranslation === "function") {
            slider.clearTranslation();
          } else if (slider.sliderWrapper) {
            slider.sliderWrapper.style.transition = "";
            slider.sliderWrapper.style.transform = "";
          }
          if (typeof slider.toggleStatus === "function") {
            slider.toggleStatus(false);
          } else {
            slider.initSliderStatus = false;
            slider.classList?.remove("slider--installed");
          }
        }

        this.removeSliderClones(slider);

        if (typeof slider.install === "function") {
          slider.install();
        }
      }

      refreshSliders() {
        if (this.viewer) {
          this.removeSliderClones(this.viewer);
          if (typeof this.viewer.reInstall === "function") {
            this.viewer.reInstall();
          } else {
            this.resetSlider(this.viewer);
          }
        }
        if (this.thumbnails) {
          this.removeSliderClones(this.thumbnails);
          if (typeof this.thumbnails.reInstall === "function") {
            this.thumbnails.reInstall();
          } else {
            this.resetSlider(this.thumbnails);
          }
        }
      }

      softRefreshSliders(featuredMediaId) {
        const soft = (slider, targetId) => {
          if (!slider) return;
          this.removeSliderClones(slider);
          if (!slider.initSliderStatus) {
            if (
              typeof slider.install === "function" &&
              this.getVisibleViewerItems().length > 1
            ) {
              slider.install();
            }
            return;
          }
          if (typeof slider.updateSliderMetrics === "function") {
            slider.updateSliderMetrics();
          }
          if (typeof slider.disableTransition === "function") {
            slider.disableTransition();
          }
          if (targetId != null && typeof slider.slideByElement === "function") {
            const el =
              slider.querySelector?.(
                `[data-media-id="${targetId}"], [data-target="${targetId}"]`,
              ) || null;
            const item =
              el?.closest?.(".slider-slide") ||
              el ||
              (slider === this.thumbnails
                ? this.findThumbnailByMediaId(targetId)
                : this.findViewerMediaItem(targetId));
            if (item && slider.sliderItems?.includes(item)) {
              slider.slideByElement(item, true);
            } else {
              slider.index = 0;
              if (typeof slider.performSlide === "function") {
                slider.performSlide();
              }
            }
          } else {
            slider.index = 0;
            if (typeof slider.performSlide === "function") {
              slider.performSlide();
            }
          }
          if (typeof slider.enableTransition === "function") {
            setTimeout(
              slider.enableTransition.bind(slider),
              slider.TRANSITION_DURING || 300,
            );
          }
        };

        soft(this.viewer, featuredMediaId);
        soft(this.thumbnails, featuredMediaId);
      }

      ensureVisibleImagesLoaded() {
        const roots = [this.viewer, this.thumbnails].filter(Boolean);
        roots.forEach((root) => {
          root
            .querySelectorAll("img.image-lazy-loading, img[loading='lazy'], img")
            .forEach((img) => {
              if (!this.isElementVisible(img.closest(".slider-slide") || img)) {
                return;
              }
              if (img.dataset?.srcset && !img.getAttribute("srcset")) {
                img.setAttribute("srcset", img.dataset.srcset);
                img.removeAttribute("data-srcset");
              }
              if (img.dataset?.src && (!img.getAttribute("src") || img.naturalWidth === 0)) {
                img.setAttribute("src", img.dataset.src);
              }
              if (img.loading === "lazy") {
                img.loading = "eager";
              }
              if (typeof img.decode === "function") {
                img.decode().catch(() => {});
              }
            });
        });
        if (typeof webvista !== "undefined" && webvista.initLazyImages) {
          webvista.initLazyImages();
        }
      }

      resolveVisibleMediaId(preferredId) {
        const preferred = this.viewer?.querySelector(
          `[data-media-id="${preferredId}"]`,
        );
        if (preferred && this.isElementVisible(preferred)) {
          return String(preferredId);
        }

        const groupItem = this.viewer?.querySelector(
          ".product-media-item.is-current-variant-media-group",
        );
        if (groupItem?.dataset.mediaId) {
          return groupItem.dataset.mediaId;
        }

        const current = this.viewer?.querySelector(
          ".product-media-item.is-current-variant-media",
        );
        if (current && this.isElementVisible(current)) {
          return current.dataset.mediaId;
        }

        const firstVisible = Array.from(
          this.viewer?.querySelectorAll(".product-media-item") || [],
        ).find((item) => this.isElementVisible(item));

        return firstVisible?.dataset.mediaId
          ? firstVisible.dataset.mediaId
          : String(preferredId);
      }

      getViewerList() {
        return (
          this.viewer?.querySelector(".product-media-list") ||
          this.viewer?.querySelector(
            ":scope > .slider-container > .slider-wrapper",
          ) ||
          null
        );
      }

      getThumbList() {
        if (!this.thumbnails) return null;
        return (
          this.thumbnails.querySelector(".thumbnail-list") ||
          this.thumbnails.querySelector(
            ":scope > .slider-container > .slider-wrapper",
          ) ||
          null
        );
      }

      getVisibleViewerItems() {
        const list = this.getViewerList();
        if (!list) return [];
        return Array.from(
          list.querySelectorAll(
            ":scope > .product-media-item:not(.slider-slide--clone)",
          ),
        ).filter((item) => this.isElementVisible(item));
      }

      resolveSlideToOriginal(slide) {
        if (!slide) return null;
        if (slide.hasAttribute("data-clone-from")) {
          const original = this.viewer?.querySelector(
            slide.getAttribute("data-clone-from"),
          );
          if (original) return original;
        }
        return slide;
      }

      getCurrentShownMediaId() {
        if (this.viewer?.initSliderStatus && this.viewer.sliderItems?.length) {
          const slide = this.resolveSlideToOriginal(
            this.viewer.sliderItems[this.viewer.index],
          );
          if (slide?.dataset?.mediaId) return String(slide.dataset.mediaId);
        }
        const current = this.viewer?.querySelector(
          ".product-media-item.is-current-variant-media:not(.slider-slide--clone)",
        );
        if (current && this.isElementVisible(current)) {
          return String(current.dataset.mediaId);
        }
        const first = this.getVisibleViewerItems()[0];
        return first?.dataset?.mediaId ? String(first.dataset.mediaId) : null;
      }

      captureGalleryPosition() {
        const mediaId = this.getCurrentShownMediaId();
        const visible = this.getVisibleViewerItems();
        let index = 0;
        if (mediaId) {
          const found = visible.findIndex(
            (item) => String(item.dataset.mediaId) === String(mediaId),
          );
          if (found >= 0) index = found;
        }
        return { mediaId, index };
      }

      findThumbnailByMediaId(mediaId) {
        if (!this.thumbnails || mediaId == null) return null;
        const matches = Array.from(
          this.thumbnails.querySelectorAll(`[data-target="${mediaId}"]`),
        );
        return (
          matches.find(
            (item) => !item.classList.contains("slider-slide--clone"),
          ) ||
          matches[0] ||
          null
        );
      }

      syncActiveThumbnail(mediaId, { scrollIntoView = true } = {}) {
        const thumbnail = this.findThumbnailByMediaId(mediaId);
        if (!thumbnail) return;
        this.setActiveThumbnail(thumbnail);
        if (
          scrollIntoView &&
          this.thumbnails?.initSliderStatus &&
          typeof this.thumbnails.moveElementToOptimalPosition === "function"
        ) {
          this.thumbnails.moveElementToOptimalPosition(thumbnail);
        }
      }

      getVisibleThumbItems() {
        const list = this.getThumbList();
        if (!list) return [];
        return Array.from(
          list.querySelectorAll(
            ":scope > .thumbnail-list-item:not(.slider-slide--clone)",
          ),
        ).filter((item) => this.isElementVisible(item));
      }

      onSlideChanged(event) {
        if (!this.thumbnails || !event?.detail?.currentElement) return;
        if (this.viewer && event.target !== this.viewer) return;

        const current = this.resolveSlideToOriginal(
          event.detail.currentElement,
        );
        const mediaId = current?.dataset?.mediaId;
        if (!mediaId) return;

        const visibleMain = this.getVisibleViewerItems();
        let mainIndex = visibleMain.findIndex((item) => item === current);
        if (mainIndex < 0) {
          mainIndex = visibleMain.findIndex(
            (item) => String(item.dataset.mediaId) === String(mediaId),
          );
        }
        const thumbs = this.getVisibleThumbItems();
        const preferredThumb =
          mainIndex >= 0 && thumbs[mainIndex]
            ? thumbs[mainIndex]
            : this.findThumbnailByMediaId(mediaId);

        if (preferredThumb) {
          this.setActiveThumbnail(preferredThumb);
          if (
            this.thumbnails?.initSliderStatus &&
            typeof this.thumbnails.moveElementToOptimalPosition === "function"
          ) {
            this.thumbnails.moveElementToOptimalPosition(preferredThumb);
          }
        } else {
          this.syncActiveThumbnail(mediaId, { scrollIntoView: true });
        }
      }

      onThumbnailClick(event) {
        event.preventDefault?.();
        event.stopPropagation?.();
        this.externalSyncPausedUntil = Date.now() + 1200;
        this.galleryUpdateToken += 1;
        let thumbnail =
          event.currentTarget?.closest?.("[data-target]") ||
          event.currentTarget;
        if (!thumbnail) return;

        if (
          thumbnail.classList.contains("slider-slide--clone") &&
          thumbnail.hasAttribute("data-clone-from")
        ) {
          const original = this.thumbnails?.querySelector(
            thumbnail.getAttribute("data-clone-from"),
          );
          if (original) thumbnail = original;
        }

        const mediaId = thumbnail.getAttribute("data-target");
        if (!mediaId) return;

        this.restoreAllGuardedImages();
        const thumbs = this.getVisibleThumbItems();
        let thumbIndex = thumbs.findIndex((item) => item === thumbnail);
        if (thumbIndex < 0 && this.thumbnails?.sliderItems?.length) {
          thumbIndex = this.thumbnails.sliderItems.indexOf(thumbnail);
        }

        this.setActiveThumbnail(thumbnail);
        this.navigateToGalleryIndex(thumbIndex, mediaId, {
          silent: false,
          strictIndex: true,
        });
      }

      navigateToGalleryIndex(index, mediaId, options = {}) {
        const silent = options.silent === true;
        const strictIndex = options.strictIndex === true;
        this.ensureViewerReady();

        if (this.viewer?.initSliderStatus) {
          if (typeof this.viewer.updateSliderMetrics === "function") {
            this.viewer.updateSliderMetrics();
          }
        }

        let target = null;
        const hasIndex = index != null && index >= 0;

        if (hasIndex && this.viewer?.sliderItems?.[index]) {
          target = this.viewer.sliderItems[index];
        }

        if (!target && hasIndex) {
          const visibles = this.getVisibleViewerItems();
          target = visibles[index] || null;
        }

        if (!target && !strictIndex && mediaId != null) {
          target = this.findViewerMediaItem(mediaId);
        }

        if (!target) return false;

        return this.slideViewerToElement(target, silent, {
          forceIndex: hasIndex ? index : null,
        });
      }

      slideViewerToElement(target, silent = false, options = {}) {
        if (!target) return false;
        const forceIndex =
          options.forceIndex != null && options.forceIndex >= 0
            ? options.forceIndex
            : null;

        if (this.viewer?.initSliderStatus) {
          if (typeof this.viewer.updateSliderMetrics === "function") {
            this.viewer.updateSliderMetrics();
          }

          const items = this.viewer.sliderItems || [];
          let idx = items.indexOf(target);

          if (idx < 0 && forceIndex != null && items[forceIndex]) {
            idx = forceIndex;
            target = items[forceIndex];
          }

          if (idx < 0) {
            idx = items.findIndex(
              (slide) =>
                !slide.classList.contains("slider-slide--clone") &&
                slide === target,
            );
          }

          if (idx >= 0 && typeof this.viewer.performSlide === "function") {
            this.viewer.index = idx;
            if (silent && typeof this.viewer.disableTransition === "function") {
              this.viewer.disableTransition();
            }
            this.viewer.performSlide();
            if (items[this.viewer.index] !== target && items[idx]) {
              this.viewer.index = idx;
              this.viewer.updateCurrentTranslate?.();
              this.viewer.applyTranslation?.();
              this.viewer.updateInfo?.();
            }
            if (silent && typeof this.viewer.enableTransition === "function") {
              setTimeout(
                this.viewer.enableTransition.bind(this.viewer),
                this.viewer.TRANSITION_DURING || 300,
              );
            }
            this.verifyHeroPosition(target, silent);
            return true;
          }

          if (typeof this.viewer.slideByElement === "function") {
            const ok = this.viewer.slideByElement(target, silent) !== false;
            if (ok) {
              this.verifyHeroPosition(target, silent);
              return true;
            }
          }
          return this.manualTranslateToTarget(target, silent);
        }

        if (this.getVisibleViewerItems().length > 1) {
          return this.manualTranslateToTarget(target, silent);
        }

        if (typeof this.viewer?.clearTranslation === "function") {
          this.viewer.clearTranslation();
        }
        return true;
      }

      getVisibleStackItems() {
        return this.stackMediaItems.filter((item) => {
          if (
            item.classList.contains("product-media-item--variant") &&
            !item.classList.contains("is-current-variant-media")
          ) {
            return false;
          }

          if (
            item.hasAttribute("data-media-group") &&
            !item.classList.contains("is-current-variant-media-group")
          ) {
            return false;
          }

          return true;
        });
      }

      syncStackSeeMore() {
        const stackSeeMore = this.stackSeeMore;
        const stackSeeMoreOpener = this.stackSeeMoreOpener;

        if (!stackSeeMore || !stackSeeMoreOpener) return;

        this.stackMediaItems.forEach((item) => {
          item.classList.remove("product-media-item--stack-hidden");
        });

        if (!this.ifMediaStack || webvista.isMobileScreen()) {
          stackSeeMore.hidden = true;
          stackSeeMoreOpener.removeAttribute("data-media-id");
          return;
        }

        const visibleItems = this.getVisibleStackItems();
        const firstHiddenItem = visibleItems[this.stackSeeMoreLimit];

        if (!firstHiddenItem) {
          stackSeeMore.hidden = true;
          stackSeeMoreOpener.removeAttribute("data-media-id");
          return;
        }

        visibleItems.slice(this.stackSeeMoreLimit).forEach((item) => {
          item.classList.add("product-media-item--stack-hidden");
        });

        stackSeeMoreOpener.setAttribute(
          "data-media-id",
          firstHiddenItem.dataset.mediaId,
        );
        stackSeeMore.hidden = false;
      }

      updateGallery(currentVariantMediaId, colorValue, variant) {
        if (currentVariantMediaId == null && currentVariantMediaId !== 0) {
          return;
        }

        const token = ++this.galleryUpdateToken;
        const featuredMediaId = String(currentVariantMediaId);
        this.lastVariantMediaId = Number(featuredMediaId) || featuredMediaId;
        this.restoreAllGuardedImages();

        this.viewer
          ?.querySelectorAll(".is-current-variant-media")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media"),
          );
        this.viewer
          ?.querySelectorAll(
            `.product-media-item[data-media-id="${featuredMediaId}"]`,
          )
          .forEach((element) =>
            element.classList.add("is-current-variant-media"),
          );

        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll(".is-current-variant-thumbnail")
            .forEach((element) =>
              element.classList.remove("is-current-variant-thumbnail"),
            );
          const currentThumb = this.findThumbnailByMediaId(featuredMediaId);
          if (currentThumb) {
            currentThumb.classList.add("is-current-variant-thumbnail");
          }
        }

        if (this.hasAttribute("data-hide-variants")) {
          const groupName = this.resolveActiveGroup(
            colorValue,
            featuredMediaId,
            variant,
          );
          this.applyActiveGroup(groupName);
          void this.offsetHeight;

          if (this.ifMediaStack && !webvista.isMobileScreen()) {
            this.syncStackSeeMore();
          }
        }

        this.reorderFeaturedToFront(featuredMediaId);
        this.ensureVisibleImagesLoaded();

        if (token !== this.galleryUpdateToken) return;

        if (this.ifMediaStack) {
          this.syncStackSeeMore();
          this.focusFeaturedMedia(featuredMediaId);
          if (webvista.isMobileScreen()) {
            webvista.scrollElementToHeaderBottom(this, 16);
          }
          return;
        }

        this.softRefreshSliders(featuredMediaId);
        if (token !== this.galleryUpdateToken) return;
        this.focusFeaturedMedia(featuredMediaId);

        if (webvista.isMobileScreen()) {
          webvista.scrollElementToHeaderBottom(this, 16);
        }
      }

      focusFeaturedMedia(featuredMediaId) {
        const mediaId = String(featuredMediaId);
        this.navigateToMedia(mediaId, { silent: true });

        if (this.viewer?.initSliderStatus) {
          if (typeof this.viewer.updateSliderMetrics === "function") {
            this.viewer.updateSliderMetrics();
          }
          const idx = (this.viewer.sliderItems || []).findIndex(
            (slide) =>
              !slide.classList.contains("slider-slide--clone") &&
              String(slide.dataset.mediaId) === mediaId,
          );
          if (idx >= 0) {
            this.viewer.index = idx;
            if (typeof this.viewer.disableTransition === "function") {
              this.viewer.disableTransition();
            }
            this.viewer.performSlide();
            if (typeof this.viewer.enableTransition === "function") {
              setTimeout(
                this.viewer.enableTransition.bind(this.viewer),
                this.viewer.TRANSITION_DURING || 300,
              );
            }
          }
        }

        const firstThumb = this.findThumbnailByMediaId(mediaId);
        if (this.thumbnails?.initSliderStatus) {
          if (typeof this.thumbnails.updateSliderMetrics === "function") {
            this.thumbnails.updateSliderMetrics();
          }
          if (typeof this.thumbnails.clearTranslation === "function") {
            this.thumbnails.clearTranslation();
          }
          if (firstThumb && typeof this.thumbnails.slideByElement === "function") {
            this.thumbnails.slideByElement(firstThumb, true);
          } else {
            this.thumbnails.index = 0;
            if (typeof this.thumbnails.performSlide === "function") {
              this.thumbnails.performSlide();
            }
          }
        }

        this.syncActiveThumbnail(mediaId, { scrollIntoView: false });
      }

      reorderFeaturedToFront(mediaId) {
        const list = this.getViewerList();
        const activeMedia = this.findViewerMediaItem(mediaId);
        if (list && activeMedia) {
          list.prepend(activeMedia);
        }

        const thumbList = this.getThumbList();
        const thumb = this.findThumbnailByMediaId(mediaId);
        if (thumbList && thumb) {
          thumbList.prepend(thumb);
        }

        void this.offsetHeight;
      }

      findViewerMediaItem(mediaId) {
        const list = this.getViewerList();
        if (!list || mediaId == null) return null;

        return (
          Array.from(
            list.querySelectorAll(
              ":scope > .product-media-item:not(.slider-slide--clone)",
            ),
          ).find((item) => String(item.dataset.mediaId) === String(mediaId)) ||
          null
        );
      }

      ensureViewerReady() {
        const viewer = this.viewer;
        if (!viewer) return false;

        if (
          !viewer.initSliderStatus &&
          typeof viewer.install === "function" &&
          this.getVisibleViewerItems().length > 1
        ) {
          viewer.install();
        }

        return !!viewer.initSliderStatus;
      }

      manualTranslateToTarget(target, silent) {
        const viewer = this.viewer;
        const wrapper =
          viewer?.querySelector(".slider-wrapper") || this.getViewerList();
        const container = viewer?.querySelector(".slider-container");
        if (!viewer || !wrapper || !container || !target) return false;

        const visible = this.getVisibleViewerItems();
        if (!visible.length) return false;

        const first = visible[0];
        const firstRect = first.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        let offset = targetRect.left - firstRect.left;
        const maxOffset = Math.max(
          0,
          visible[visible.length - 1].getBoundingClientRect().right -
            firstRect.left -
            containerRect.width,
        );
        offset = Math.min(Math.max(0, offset), maxOffset);

        if (silent) wrapper.style.transition = "none";
        wrapper.style.transform = `translate3d(${-offset}px, 0, 0)`;
        if (silent) {
          setTimeout(() => {
            wrapper.style.transition = "";
          }, 320);
        }
        return true;
      }

      navigateToMedia(mediaId, options = {}) {
        const silent = options.silent === true;
        if (mediaId == null) return false;

        const mediaKey = String(mediaId);
        this.ensureViewerReady();

        if (options.index != null) {
          return this.navigateToGalleryIndex(options.index, mediaKey, {
            silent,
          });
        }

        let target = null;
        if (
          this.viewer?.initSliderStatus &&
          Array.isArray(this.viewer.sliderItems)
        ) {
          target = this.viewer.sliderItems.find(
            (slide) =>
              !slide.classList.contains("slider-slide--clone") &&
              String(slide.dataset.mediaId) === mediaKey,
          );
        }

        if (!target) {
          target = this.findViewerMediaItem(mediaKey);
        }

        if (!target) {
          const list = this.getViewerList();
          target = Array.from(
            list?.querySelectorAll(":scope > .product-media-item") || [],
          ).find((item) => String(item.dataset.mediaId) === mediaKey);
        }
        if (!target) return false;

        return this.slideViewerToElement(target, silent);
      }

      verifyHeroPosition(target, silent) {
        const token = this.galleryUpdateToken;
        const check = () => {
          if (token !== this.galleryUpdateToken) return false;
          const container = this.viewer?.querySelector(".slider-container");
          if (!container || !target?.isConnected) return false;
          const cRect = container.getBoundingClientRect();
          const tRect = target.getBoundingClientRect();
          if (cRect.width <= 0) return false;

          const atLeft = Math.abs(tRect.left - cRect.left) <= 8;
          const atRightEdge =
            Math.abs(tRect.right - cRect.right) <= 8 &&
            tRect.left <= cRect.left + 8;
          if (atLeft || atRightEdge) return false;
          return true;
        };

        const fix = () => {
          if (token !== this.galleryUpdateToken) return;
          if (!check()) return;
          if (this.viewer?.initSliderStatus && this.viewer.metricsLookStale?.()) {
            const idx = (this.viewer.sliderItems || []).indexOf(target);
            this.viewer.updateSliderMetrics?.();
            const next = (this.viewer.sliderItems || []).indexOf(target);
            this.viewer.index = next >= 0 ? next : Math.max(idx, 0);
            this.viewer.disableTransition?.();
            this.viewer.performSlide?.();
            setTimeout(
              () => this.viewer.enableTransition?.(),
              this.viewer.TRANSITION_DURING || 300,
            );
            setTimeout(() => {
              if (token !== this.galleryUpdateToken) return;
              if (check()) this.manualTranslateToTarget(target, true);
            }, 80);
            return;
          }
          this.manualTranslateToTarget(target, true);
        };

        setTimeout(fix, silent ? 60 : 280);
      }

      changeMedia(mediaId, options = {}) {
        this.navigateToMedia(mediaId, {
          silent: options.silent === true,
        });
        if (options.syncThumb !== false) {
          this.syncActiveThumbnail(mediaId, {
            scrollIntoView: options.scrollThumb !== false,
          });
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        const resolved =
          thumbnail.classList.contains("slider-slide--clone") &&
          thumbnail.hasAttribute("data-clone-from")
            ? this.thumbnails.querySelector(
                thumbnail.getAttribute("data-clone-from"),
              ) || thumbnail
            : thumbnail;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        const mediaButton = resolved.matches?.(".thumbnail-media")
          ? resolved
          : resolved.querySelector(".thumbnail-media") ||
            resolved.querySelector("button");
        mediaButton?.setAttribute("aria-current", "true");
      }
    },
  );
}
